/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-unsafe-call */
import {
  GET_ALL_ORGANIZATIONS_QUERY,
  GET_ORGANIZATION_BY_ID_QUERY,
  CREATE_ORGANIZATION_MUTATION,
  UPDATE_ORGANIZATION_MUTATION,
  ARCHIVE_ORGANIZATION_MUTATION,
  ADD_USER_TO_ORGANIZATION_MUTATION,
  UPDATE_ORG_USER_MUTATION,
  UPDATE_ORG_PRIMARY_CONTACT_MUTATION,
  REMOVE_USER_FROM_ORGANIZATION_MUTATION,
  GET_ALL_CITIES_WITH_COUNTRY_QUERY,
  GET_ALL_CITY_GROUPS_QUERY,
  SEND_TEST_EXPIRY_NOTIFICATION_MUTATION,
} from '../../../constants/QueryTypes'
import query from '../../../services/GraphQLService'
import { City, CityGroup } from '../types'

interface OrganizationListItem {
  id: number
  name: string
  contact: string
  user_count: number
  city_count: number
  status: string
  subscription_start: string
  subscription_expiry: string
}

interface OrganizationUser {
  id: number
  first_name: string
  last_name: string
  email: string
  occupation: string
  phone: string
  role_title: string
  date_assigned: string | null
}

interface CityAssignment {
  city_id: number
  date_assigned: string | null
}

interface OrganizationDetail {
  id: number
  name: string
  address1: string
  address2: string
  city: string
  country: string
  province: string
  postal_code: string
  org_type: string
  subscription_type: string
  subscription_start: string
  subscription_expiry: string
  billing_notes: string
  city_ids: number[]
  city_assignments: CityAssignment[]
  primary_contact_id: number | null
  archived: boolean
  users: OrganizationUser[]
}

interface MutationResponse {
  success: boolean
  message: string
  id: number
}

export const getAllOrganizations = async (
  archived = false
): Promise<OrganizationListItem[]> => {
  const response = await query('query', GET_ALL_ORGANIZATIONS_QUERY, {
    archived,
  })
  return response!.data.GetAllOrganizations
}

export const getOrganizationById = async (
  id: number
): Promise<OrganizationDetail> => {
  const response = await query('query', GET_ORGANIZATION_BY_ID_QUERY, { id })
  return response!.data.GetOrganizationById
}

export const createOrganization = async (
  orgData: Record<string, unknown>
): Promise<MutationResponse> => {
  const response = await query('mutate', CREATE_ORGANIZATION_MUTATION, orgData)
  return response!.data.createOrganization
}

export const updateOrganization = async (
  orgData: Record<string, unknown>
): Promise<MutationResponse> => {
  const response = await query('mutate', UPDATE_ORGANIZATION_MUTATION, orgData)
  return response!.data.updateOrganization
}

export const archiveOrganization = async (
  id: number,
  archived = true
): Promise<MutationResponse> => {
  const response = await query('mutate', ARCHIVE_ORGANIZATION_MUTATION, {
    id,
    archived,
  })
  return response!.data.archiveOrganization
}

export const addUserToOrganization = async (
  userData: Record<string, unknown>
): Promise<MutationResponse> => {
  const response = await query(
    'mutate',
    ADD_USER_TO_ORGANIZATION_MUTATION,
    userData
  )
  return response!.data.addUserToOrganization
}

export const updateOrgUser = async (
  userData: Record<string, unknown>
): Promise<MutationResponse> => {
  const response = await query('mutate', UPDATE_ORG_USER_MUTATION, userData)
  return response!.data.updateOrgUser
}

export const removeUserFromOrganization = async (
  userId: number
): Promise<MutationResponse> => {
  const response = await query(
    'mutate',
    REMOVE_USER_FROM_ORGANIZATION_MUTATION,
    { userId }
  )
  return response!.data.removeUserFromOrganization
}

export const updateOrgPrimaryContact = async (
  orgId: number,
  userId: number
): Promise<MutationResponse> => {
  const response = await query('mutate', UPDATE_ORG_PRIMARY_CONTACT_MUTATION, {
    orgId,
    userId,
  })
  return response!.data.updateOrgPrimaryContact
}

export const getAllCities = async (): Promise<City[]> => {
  const response = await query('query', GET_ALL_CITIES_WITH_COUNTRY_QUERY)
  return (response!.data.AllCities ?? []).map(
    (c: {
      id: number
      name: string
      country?: string
      province?: string
      profile?: { lang?: Record<string, unknown> }
    }) => ({
      id: c.id,
      name: c.name,
      state: c.province ?? '',
      country: c.country ?? '',
      continent: '',
      lang: c.profile?.lang ?? null,
    })
  )
}

export const getAllCityGroups = async (): Promise<CityGroup[]> => {
  const response = await query('query', GET_ALL_CITY_GROUPS_QUERY)
  return (response!.data.AllCityGroups ?? []).map(
    (g: {
      id: number
      name: string
      description?: string
      cities?: number[]
    }) => ({
      id: g.id,
      name: g.name,
      description: g.description ?? '',
      cities: g.cities ?? [],
    })
  )
}

export const sendTestExpiryNotification = async (
  orgId: number,
  days: number
): Promise<MutationResponse> => {
  const response = await query(
    'mutate',
    SEND_TEST_EXPIRY_NOTIFICATION_MUTATION,
    { orgId, days }
  )
  return response!.data.sendTestExpiryNotification
}
